import axios from 'axios';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import {
  Cake,
  ChevronDown,
  ChevronUp,
  Home,
  Key,
  Mail,
  Pencil,
  School,
  Settings,
  FolderPlus,
} from 'lucide-react';

import { Fragment, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Link } from 'react-router-dom';

import { backendHOST } from '../../client-config/client-config.js';
import def_avatar from '../../img/def_avatar.png';
import fiu_avatar_2 from '../../img/IT_Fiu2.png';
import lany_avatar_szoke from '../../img/IT_Lany1.png';
import lany_avatar_barna from '../../img/IT_Lany2.png';

import { MessagePopup } from '../forms/TaskManagement/MessagePopup.js';
import { TaskCreatePopup } from '../forms/TaskManagement/TaskCreatePopup.js';

const createEmptyTask = () => ({
  createdByUser: 0,
  task_title: '',
  task_descr: '',
  task_object: '',
  task_key: '',
  task_recipient: '',
});

const ProfileBox = ({ profile, tasks, user }) => {
  const isMobile = useMediaQuery({ query: `(max-width: 760px)` });

  const [displayProfileBox, setDisplayProfileBox] = useState(!isMobile);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [taskCreateVisible, setTaskCreateVisible] = useState(false);
  const [task, setTask] = useState(createEmptyTask);

  const taskVisible = useMemo(() => {
    return Array.isArray(tasks) && tasks.length > 0;
  }, [tasks]);

  const getAvatar = (type) => {
    switch (type) {
      case 'F':
        return lany_avatar_barna;
      case 'M':
        return fiu_avatar_2;
      default:
        return lany_avatar_szoke;
    }
  };

  const handleMessageDialogClose = () => {
    setMessageDialogOpen(false);
  };

  const handleTaskCreateClose = () => {
    setTaskCreateVisible(false);
  };

  const handleTaskCreate = async () => {
    try {
      await axios.post(`${backendHOST}/api/taskManager/createTask`, task, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      setTask(createEmptyTask());
      setTaskCreateVisible(false);
    } catch (err) {
      alert('Hiba a Task generálás során');
      console.error(err);
    }
  };

  if (!profile) {
    return (
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack alignItems='center' spacing={2}>
            <Typography variant='h6' fontWeight={700}>
              Még nincs profil
            </Typography>

            <Avatar
              src={def_avatar}
              alt='Avatar'
              sx={{ width: 106, height: 106 }}
            />

            <Divider flexItem />

            <Button
              component={Link}
              to='/create-profile'
              startIcon={<Settings size={18} />}
            >
              Profil létrehozása
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Fragment>
      <Card sx={{ borderRadius: 3 }} backgroundColor='#fff'>
        <CardContent>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
          >
            <Typography
              variant='h6'
              fontWeight={700}
              sx={{ flex: 1, textAlign: 'center' }}
            >
              {profile.lastName} {profile.firstName}
            </Typography>

            <IconButton onClick={() => setDisplayProfileBox((prev) => !prev)}>
              {displayProfileBox ? <ChevronUp /> : <ChevronDown />}
            </IconButton>
          </Stack>

          <Collapse in={displayProfileBox}>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Stack direction='row' spacing={2} alignItems='center'>
                <IconButton component={Link} to='UserParams'>
                  <Settings size={20} />
                </IconButton>

                <Box sx={{ flex: 1, textAlign: 'center' }}>
                  <Avatar
                    src={getAvatar(profile.sex)}
                    alt='Avatar'
                    sx={{
                      width: 106,
                      height: 106,
                      mx: 'auto',
                    }}
                  />
                </Box>

                <Stack spacing={1} sx={{ minWidth: 130 }}>
                  <Button
                    variant='contained'
                    startIcon={<FolderPlus size={18} />}
                    onClick={() => setTaskCreateVisible(true)}
                    fullWidth
                  >
                    Feladat
                  </Button>

                  {taskVisible && (
                    <Button
                      variant='outlined'
                      startIcon={<Mail size={18} />}
                      onClick={() => setMessageDialogOpen(true)}
                      fullWidth
                    >
                      Üzenet
                    </Button>
                  )}
                </Stack>
              </Stack>

              <Divider />

              <Stack spacing={1.5}>
                <ProfileInfo
                  icon={<School size={18} />}
                  text={profile.school}
                />

                <ProfileInfo
                  icon={<Home size={18} />}
                  text={`${profile.zip ?? ''} ${profile.city ?? ''} ${
                    profile.address ?? ''
                  }`}
                />

                <ProfileInfo
                  icon={<Cake size={18} />}
                  text={profile.birthday}
                />

                <ProfileInfo
                  icon={<Pencil size={18} />}
                  text={profile.aboutme}
                />

                <Button
                  component={Link}
                  to='create-profile'
                  startIcon={<Settings size={18} />}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Profil szerkesztése
                </Button>

                <Button
                  component={Link}
                  to='change-password'
                  startIcon={<Key size={18} />}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Jelszó módosítása
                </Button>
              </Stack>
            </Stack>
          </Collapse>
        </CardContent>
      </Card>

      <Dialog
        open={messageDialogOpen}
        onClose={handleMessageDialogClose}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>Elvégzendő feladatok</DialogTitle>

        <DialogContent dividers>
          <MessagePopup popupTasks={tasks} user={user} />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleMessageDialogClose}>Bezár</Button>
        </DialogActions>
      </Dialog>

      <TaskCreatePopup
        taskPopupVisible={taskCreateVisible}
        taskPosition=''
        buttonOption={{
          text: 'Bezár',
          onClick: handleTaskCreateClose,
        }}
        createTaskOptions={{
          text: 'Létrehoz',
          onClick: handleTaskCreate,
        }}
        toCreateTask={task}
        hideEvent={handleTaskCreateClose}
      />
    </Fragment>
  );
};

const ProfileInfo = ({ icon, text }) => {
  if (!text) return null;

  return (
    <Stack direction='row' spacing={1.5} alignItems='center'>
      <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
      <Typography variant='body2'>{text}</Typography>
    </Stack>
  );
};

export default ProfileBox;
